import type { ResourcesResponse, ResourceTypeItem, ResourceUnit } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, post, put } from "../../api";
import { Alert, Badge, Button, Dialog, Field, Loading, Panel, PanelHeader, SelectField, Checkbox } from "../../components/ui";

export function useResources() {
  return useQuery({ queryKey: ["resources"], queryFn: () => get<ResourcesResponse>("/api/resources") });
}

const fmt = (local: string) =>
  new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }).format(new Date(`${local}:00Z`));

export function ResourcesTab() {
  const data = useResources();
  const [editingType, setEditingType] = useState<ResourceTypeItem | "new" | null>(null);
  const [editingUnit, setEditingUnit] = useState<{ type: ResourceTypeItem; unit: ResourceUnit | null } | null>(null);

  if (data.isLoading) return <Loading />;
  if (data.error || !data.data) return <Alert>{errorMessage(data.error)}</Alert>;
  const { types } = data.data;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-ink-soft">
        Equipment that clinic visits need, like chairs and compression systems. Services list what they need under Catalog; booking will only offer times when it's free.
      </p>
      {types.map((t) => (
        <Panel key={t.id}>
          <PanelHeader
            title={t.name}
            description={t.description || undefined}
            action={
              <div className="flex items-center gap-2">
                {!t.active && <Badge tone="off">Retired</Badge>}
                <Button variant="quiet" onClick={() => setEditingType(t)}>
                  Edit type
                </Button>
              </div>
            }
          />
          <ul className="divide-y divide-mist">
            {t.units.map((u) => (
              <li key={u.id} className="flex items-center justify-between gap-3 px-5 py-2.5">
                <span className="font-medium">
                  {u.name} {!u.active && <Badge tone="off">Retired</Badge>}
                </span>
                <Button variant="quiet" onClick={() => setEditingUnit({ type: t, unit: u })}>
                  Edit
                </Button>
              </li>
            ))}
          </ul>
          <div className="border-t border-mist px-5 py-3">
            <button className="text-sm font-semibold text-brand hover:text-brand-deep" onClick={() => setEditingUnit({ type: t, unit: null })}>
              + Add {t.name.toLowerCase()}
            </button>
          </div>
        </Panel>
      ))}
      <div>
        <Button onClick={() => setEditingType("new")}>Add resource type</Button>
      </div>

      <BlocksPanel data={data.data} />

      {editingType && <TypeDialog type={editingType === "new" ? null : editingType} onClose={() => setEditingType(null)} />}
      {editingUnit && <UnitDialog type={editingUnit.type} unit={editingUnit.unit} onClose={() => setEditingUnit(null)} />}
    </div>
  );
}

function useSave(onDone: () => void) {
  const qc = useQueryClient();
  return async (p: Promise<ResourcesResponse>) => {
    qc.setQueryData(["resources"], await p);
    onDone();
  };
}

function TypeDialog({ type, onClose }: { type: ResourceTypeItem | null; onClose: () => void }) {
  const [v, setV] = useState({ name: type?.name ?? "", description: type?.description ?? "", active: type?.active ?? true });
  const done = useSave(onClose);
  const save = useMutation({ mutationFn: () => done(type ? put(`/api/resource-types/${type.id}`, v) : post("/api/resource-types", v)) });
  return (
    <Dialog
      title={type ? "Edit resource type" : "Add resource type"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="type-form" busy={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="type-form" className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), save.mutate())}>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name" required maxLength={60} autoFocus placeholder="Chair" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        <Field label="Description (optional)" maxLength={300} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} />
        {type && <Checkbox label="Active" description="Retired types can't be booked." checked={v.active} onChange={(on) => setV({ ...v, active: on })} />}
      </form>
    </Dialog>
  );
}

function UnitDialog({ type, unit, onClose }: { type: ResourceTypeItem; unit: ResourceUnit | null; onClose: () => void }) {
  const [v, setV] = useState({ name: unit?.name ?? `${type.name} ${type.units.length + 1}`, active: unit?.active ?? true });
  const done = useSave(onClose);
  const body = { typeId: type.id, name: v.name, locationId: unit?.locationId ?? null, active: v.active };
  const save = useMutation({ mutationFn: () => done(unit ? put(`/api/resources/${unit.id}`, body) : post("/api/resources", body)) });
  return (
    <Dialog
      title={unit ? `Edit ${unit.name}` : `Add ${type.name.toLowerCase()}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="unit-form" busy={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="unit-form" className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), save.mutate())}>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name" required maxLength={60} autoFocus value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        {unit && <Checkbox label="Active" description="Retired units can't be booked." checked={v.active} onChange={(on) => setV({ ...v, active: on })} />}
      </form>
    </Dialog>
  );
}

function BlocksPanel({ data }: { data: ResourcesResponse }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState("");
  const [form, setForm] = useState({ starts: "", ends: "", reason: "" });
  const units = data.types.flatMap((t) => t.units.map((u) => ({ ...u, typeName: t.name })));
  const label = (b: { resourceId: string | null; typeId: string | null }) =>
    b.resourceId ? (units.find((u) => u.id === b.resourceId)?.name ?? "Unknown") : `Every ${data.types.find((t) => t.id === b.typeId)?.name.toLowerCase() ?? "unit"}`;
  const add = useMutation({
    mutationFn: () => {
      const [kind, id] = target.split(":");
      return post<ResourcesResponse>("/api/resource-blocks", { ...form, resourceId: kind === "unit" ? id : null, typeId: kind === "type" ? id : null });
    },
    onSuccess: (d) => {
      qc.setQueryData(["resources"], d);
      setForm({ starts: "", ends: "", reason: "" });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => del<ResourcesResponse>(`/api/resource-blocks/${id}`),
    onSuccess: (d) => qc.setQueryData(["resources"], d),
  });

  return (
    <Panel>
      <PanelHeader title="Out of use" description="Take equipment out of use for a time: broken, being cleaned, or out at an event. Times are in the clinic's time zone." />
      <div className="flex flex-col gap-4 px-5 py-5">
        {data.blocks.length === 0 ? (
          <p className="text-sm text-muted">Nothing is blocked.</p>
        ) : (
          <ul className="divide-y divide-mist rounded-[var(--radius-control)] border border-line">
            {data.blocks.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <span>
                  <span className="font-semibold">{label(b)}</span>{" "}
                  <span className="text-sm text-ink-soft">
                    {fmt(b.starts)} to {fmt(b.ends)}
                  </span>
                  {b.reason && <span className="block text-sm text-muted">{b.reason}</span>}
                </span>
                <Button variant="quiet" busy={remove.isPending && remove.variables === b.id} onClick={() => remove.mutate(b.id)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <form className="grid items-end gap-3 sm:grid-cols-2" onSubmit={(e) => (e.preventDefault(), add.mutate())}>
          <SelectField label="What" required value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose…</option>
            {data.types.map((t) => (
              <optgroup key={t.id} label={t.name}>
                <option value={`type:${t.id}`}>Every {t.name.toLowerCase()}</option>
                {t.units.map((u) => (
                  <option key={u.id} value={`unit:${u.id}`}>
                    {u.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </SelectField>
          <Field label="Reason (optional)" maxLength={200} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Field label="From" type="datetime-local" required value={form.starts} onChange={(e) => setForm({ ...form, starts: e.target.value })} />
          <Field label="Until" type="datetime-local" required value={form.ends} onChange={(e) => setForm({ ...form, ends: e.target.value })} />
          {add.error && (
            <div className="sm:col-span-2">
              <Alert>{errorMessage(add.error)}</Alert>
            </div>
          )}
          <div className="sm:col-span-2 flex justify-end">
            <Button variant="primary" type="submit" busy={add.isPending}>
              Block
            </Button>
          </div>
        </form>
      </div>
    </Panel>
  );
}
